import type { TabProps, TabsProps, TabsActions } from '@mui/material';
import { Tab, Tabs } from '@mui/material';
import type { Permission } from '@vy/types';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import React, { useEffect } from 'react';
import { renderWithPermissions } from '../../../../services/render-with-permissions';

export type VyTabProps = TabProps & {
  permissions?: Permission[];
};

export type VyPageLayoutRequiredTabsProps = {
  tabs?: VyTabProps[];
  currentTab?: string;
};

export type VyPageLayoutTabsProps = TabsProps &
  VyPageLayoutRequiredTabsProps & {
    tabsAction?: React.ReactNode;
  };

export const VyPageLayoutTabs: React.FC<
  VyPageLayoutTabsProps & { containerRef: React.RefObject<HTMLDivElement> }
> = ({ tabs, currentTab, containerRef, centered = false, tabsAction, ...tabsProps }) => {
  const actionRef = useTabsIndicatorPageResizeUpdate(containerRef, tabs, currentTab);

  if (isNullOrUndefined(tabs) || isNullOrUndefined(currentTab)) {
    return null;
  }

  return (
    <Tabs centered={centered} value={currentTab} action={actionRef} {...tabsProps}>
      {tabs.map((tab, index) =>
        renderWithPermissions(tab.permissions, (hasPermission) => (
          <Tab {...tab} key={index} disabled={!hasPermission} />
        )),
      )}

      {tabsAction}
    </Tabs>
  );
};

function useTabsIndicatorPageResizeUpdate(
  containerRef: React.RefObject<HTMLDivElement>,
  tabs: TabProps[] | undefined,
  currentTab: string | undefined,
) {
  const actionRef = React.useRef<TabsActions | null>(null);

  useEffect(() => {
    const copiedContainerRef = { ...containerRef };

    if (isNullOrUndefined(tabs) || isNullOrUndefined(currentTab)) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (actionRef.current && copiedContainerRef.current) {
        actionRef.current.updateIndicator();
      }
    });

    if (copiedContainerRef.current) {
      resizeObserver.observe(copiedContainerRef.current);
    }

    return () => {
      isNotNullOrUndefined(copiedContainerRef.current) &&
        resizeObserver.unobserve(copiedContainerRef.current);
    };
  }, [tabs, currentTab, containerRef]);

  return actionRef;
}
